// DashboardDoc (doc 06 §6) and the dashboard API contract (doc 08 §6).
import { z } from 'zod';
import { orgRoleSchema } from './auth';
import { objectIdSchema } from './common';
import { AGGREGATIONS, bindingSchema, timeExpressionSchema } from './data';

export const DASHBOARD_SCHEMA_VERSION = 1;

export const WIDGET_TYPES = ['trend', 'valueCard', 'gauge', 'statusLamp', 'liveTable', 'periodTable', 'seqLog', 'text', 'bar', 'xy', 'map', 'image', 'schematic', 'wellsTable', 'link', 'stateTimeline'] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const thresholdSchema = z.object({
  op: z.enum(['>', '>=', '<', '<=']),
  value: z.number(),
  /** Unit the threshold value is expressed in; converted to the display unit when applied. */
  unitId: z.string().max(64).nullable().optional(),
  color: z.enum(['success', 'warning', 'danger', 'info']),
});
export type Threshold = z.infer<typeof thresholdSchema>;

/** Colours a mapping can give a value: the threshold levels, or none (the text alone). */
export const MAPPING_COLORS = ['success', 'info', 'warning', 'danger', 'neutral'] as const;
/**
 * Value mapping: a number shown as a word (state 16 → "COOLDOWN"), optionally with a colour.
 * `value` matches one number exactly; `range` matches from…to, both included, either side open.
 */
export const valueMappingSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('value'), value: z.number(), text: z.string().trim().max(60), color: z.enum(MAPPING_COLORS).optional() }),
  z.object({ type: z.literal('range'), from: z.number().nullable(), to: z.number().nullable(), text: z.string().trim().max(60), color: z.enum(MAPPING_COLORS).optional() }),
]);
export type ValueMapping = z.infer<typeof valueMappingSchema>;

/** A step applied to a series' values in the browser, in order, after unit conversion. */
const ratePer = z.enum(['s', 'min', 'h', 'd']);
export const transformStepSchema = z.discriminatedUnion('op', [
  /** value × factor + offset */
  z.object({ op: z.literal('scale'), factor: z.number().default(1), offset: z.number().default(0) }),
  /** Bit n of the whole value: 0 or 1 (bit 0 is the lowest). */
  z.object({ op: z.literal('bit'), bit: z.number().int().min(0).max(52) }),
  /** Whole value AND mask. */
  z.object({ op: z.literal('mask'), mask: z.number().int().min(0).max(2 ** 53 - 1) }),
  z.object({ op: z.literal('round'), decimals: z.number().int().min(0).max(8).default(0) }),
  z.object({ op: z.literal('abs') }),
  z.object({ op: z.literal('clamp'), min: z.number().nullable().default(null), max: z.number().nullable().default(null) }),
  /** Mean of the points of the last `minutes`. */
  z.object({ op: z.literal('movingAvg'), minutes: z.number().min(1).max(10_080) }),
  /** Running sum of the points since the start of the range. */
  z.object({ op: z.literal('cumsum') }),
  /** Running total of a rate given per `per` (m³/h → m³), since the start of the range. */
  z.object({ op: z.literal('integral'), per: ratePer.default('h') }),
  /** Change from the previous point. */
  z.object({ op: z.literal('delta') }),
  /** Change per `per` from the previous point. */
  z.object({ op: z.literal('rate'), per: ratePer.default('h') }),
]);
export type TransformStep = z.infer<typeof transformStepSchema>;
export const TRANSFORM_OPS = ['scale', 'bit', 'mask', 'round', 'abs', 'clamp', 'movingAvg', 'cumsum', 'integral', 'delta', 'rate'] as const satisfies ReadonlyArray<TransformStep['op']>;

export const widgetSeriesSchema = z.object({
  refId: z.string().min(1).max(32),
  binding: bindingSchema,
  label: z.string().max(80).optional(),
  agg: z.enum(AGGREGATIONS).optional(),
  displayUnitId: z.string().max(64).nullable().optional(),
  decimals: z.number().int().min(0).max(8).nullable().optional(),
  color: hexColor.optional(),
  axis: z.enum(['left', 'right']).optional(),
  style: z.enum(['line', 'area', 'step', 'bar']).optional(),
  /** Trend: line width in pixels and rounding of the line (0 = straight); the widget's own when not set. */
  lineWidth: z.number().min(0.5).max(8).optional(),
  smooth: z.number().min(0).max(1).optional(),
  visible: z.boolean().optional(),
  /** Advanced: steps applied to the values, a unit text shown instead of the unit symbol (empty = none), own value mappings. */
  transforms: z.array(transformStepSchema).max(8).optional(),
  unitText: z.string().max(16).optional(),
  mappings: z.array(valueMappingSchema).max(40).optional(),
});
export type WidgetSeries = z.infer<typeof widgetSeriesSchema>;

/** Font choices of a widget: stacks of fonts every browser has, so nothing is downloaded. */
export const WIDGET_FONTS = ['default', 'system', 'serif', 'mono', 'condensed', 'rounded'] as const;
export type WidgetFont = (typeof WIDGET_FONTS)[number];
const fontWeight = z.union([z.literal(400), z.literal(500), z.literal(600), z.literal(700), z.literal(800)]);
const align = z.enum(['start', 'center', 'end']);

/**
 * How a widget looks. Every field is optional: what is not set comes from the appearance of
 * the dashboard, then from the theme of the application. Colours are fixed values, so they
 * stay the same in the light and the dark theme.
 */
export const widgetStyleSchema = z.object({
  // Card
  background: hexColor.optional(),
  borderColor: hexColor.optional(),
  borderWidth: z.number().int().min(0).max(8).optional(),
  radius: z.number().int().min(0).max(32).optional(),
  shadow: z.boolean().optional(),
  padding: z.number().int().min(0).max(32).optional(),
  fontFamily: z.enum(WIDGET_FONTS).optional(),
  // Title
  showTitle: z.boolean().optional(),
  titleColor: hexColor.optional(),
  titleBackground: hexColor.optional(),
  titleSize: z.number().int().min(8).max(48).optional(),
  titleWeight: fontWeight.optional(),
  titleAlign: align.optional(),
  titleUppercase: z.boolean().optional(),
  // Main value (value card, gauge, status lamp, value column of the tables)
  valueColor: hexColor.optional(),
  valueSize: z.number().int().min(8).max(160).optional(),
  valueWeight: fontWeight.optional(),
  valueAlign: align.optional(),
  unitColor: hexColor.optional(),
  unitSize: z.number().int().min(6).max(64).optional(),
  // Other text (tables, sequence log, text widget, chart axes and legend)
  textColor: hexColor.optional(),
  textSize: z.number().int().min(8).max(32).optional(),
  // Charts and indicators
  accentColor: hexColor.optional(),
  lineWidth: z.number().min(0.5).max(8).optional(),
  smooth: z.number().min(0).max(1).optional(),
  gridLines: z.boolean().optional(),
});
export type WidgetStyle = z.infer<typeof widgetStyleSchema>;

/** Appearance of the whole dashboard: the default of every widget, and the page behind them. */
export const dashboardStyleSchema = widgetStyleSchema.extend({ pageBackground: hexColor.optional() });
export type DashboardStyle = z.infer<typeof dashboardStyleSchema>;

/** One permissive config for every widget type: each widget reads the fields it knows. */
export const widgetConfigSchema = z.object({
  series: z.array(widgetSeriesSchema).max(24).default([]),
  thresholds: z.array(thresholdSchema).max(8).default([]),
  /** Value mappings of every series of the widget (a series' own mappings come first). */
  mappings: z.array(valueMappingSchema).max(40).optional(),
  /** Tables: the column sorted on opening (series ref id, or "time" / "name") and the direction. */
  sort: z.object({ by: z.string().max(32), dir: z.enum(['asc', 'desc']) }).nullable().optional(),
  /** Value card. */
  sparkline: z.boolean().optional(),
  /** Gauge / axis limits, in the display unit. */
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  /** Trend. */
  legend: z.boolean().optional(),
  annotations: z.boolean().optional(),
  /** Per-widget time range override, e.g. "now-1h". */
  timeFrom: z.string().regex(/^now-\d{1,5}(s|m|h|d|w)$/).nullable().optional(),
  /** Live table: extra statistic columns. */
  columns: z.array(z.enum(['min', 'max', 'mean'])).max(3).optional(),
  /** Period table: what the columns are. */
  periods: z.enum(['flowPeriods', 'hourly', 'daily']).optional(),
  /** Text widget (sanitized markdown). */
  markdown: z.string().max(10_000).optional(),
  /** Sequence log: categories shown (empty = all). */
  categories: z.array(z.string().max(16)).max(8).optional(),
  /** Image widget. */
  fileId: objectIdSchema.nullable().optional(),
  fit: z.enum(['contain', 'cover']).optional(),
  /** XY plot: shade the points from the oldest to the newest. */
  colorByTime: z.boolean().optional(),
  /** Schematic widget (Phase 6). */
  schematicId: objectIdSchema.nullable().optional(),
  /** Wells table: which wells get a row (every well the viewer can see, or those testing), optionally one field. */
  wellFilter: z.enum(['all', 'testing']).optional(),
  fieldId: objectIdSchema.nullable().optional(),
  /** Wells table: status and running-test columns. */
  showStatus: z.boolean().optional(),
  showTest: z.boolean().optional(),
  /** Link button: the dashboard it opens, its text and look, and whether the well shown goes along. */
  targetDashboardId: objectIdSchema.nullable().optional(),
  linkLabel: z.string().max(80).optional(),
  linkVariant: z.enum(['filled', 'light', 'outline']).optional(),
  passWell: z.boolean().optional(),
});
export type WidgetConfig = z.infer<typeof widgetConfigSchema>;

export const widgetSchema = z.object({
  type: z.enum(WIDGET_TYPES),
  title: z.string().max(120).default(''),
  config: widgetConfigSchema.default({}),
  style: widgetStyleSchema.optional(),
});
export type Widget = z.infer<typeof widgetSchema>;

const layoutItemSchema = z.object({
  i: z.string().min(1).max(32),
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0).max(10_000),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(60),
});
export type LayoutItem = z.infer<typeof layoutItemSchema>;

export const dashboardPageSchema = z.object({
  id: z.string().min(1).max(32),
  name: z.string().min(1).max(60),
  layout: z.object({
    lg: z.array(layoutItemSchema).max(60),
    // Empty tablet/phone layouts are generated from `lg` on load.
    md: z.array(layoutItemSchema).max(60).default([]),
    sm: z.array(layoutItemSchema).max(60).default([]),
  }),
  widgets: z.record(z.string().max(32), widgetSchema),
});
export type DashboardPage = z.infer<typeof dashboardPageSchema>;

export const dashboardVariableSchema = z.object({
  name: z.string().regex(/^[a-z][a-zA-Z0-9]{0,23}$/),
  /** 'wellsite' from before wellsites were removed is read as 'well'. */
  type: z.preprocess((v) => (v === 'wellsite' ? 'well' : v), z.enum(['field', 'well', 'test', 'list'])),
  default: z.string().max(64).optional(),
  dependsOn: z.string().max(24).optional(),
  multi: z.boolean().default(false),
  options: z.array(z.string().max(64)).max(50).optional(),
});

export const dashboardTimeSchema = z.object({
  mode: z.enum(['relative', 'absolute']).default('relative'),
  from: timeExpressionSchema.default('now-12h'),
  to: timeExpressionSchema.default('now'),
  live: z.boolean().default(true),
  liveIntervalSec: z.number().int().min(2).max(60).default(5),
});

export const dashboardDocSchema = z.object({
  schemaVersion: z.literal(DASHBOARD_SCHEMA_VERSION).default(DASHBOARD_SCHEMA_VERSION),
  variables: z.array(dashboardVariableSchema).max(10).default([]),
  time: dashboardTimeSchema.default({}),
  style: dashboardStyleSchema.optional(),
  pages: z.array(dashboardPageSchema).min(1).max(12),
});
export type DashboardDoc = z.infer<typeof dashboardDocSchema>;

export function emptyDashboardDoc(): DashboardDoc {
  return dashboardDocSchema.parse({
    variables: [{ name: 'well', type: 'well', default: 'first' }],
    pages: [{ id: 'p1', name: 'Overview', layout: { lg: [] }, widgets: {} }],
  });
}

// ---- Sharing (doc 02 §6) ----
export const dashboardShareSchema = z.object({
  principal: z.discriminatedUnion('type', [
    z.object({ type: z.literal('user'), id: objectIdSchema }),
    z.object({ type: z.literal('role'), id: orgRoleSchema }),
  ]),
  access: z.enum(['view', 'edit']),
});
export type DashboardShare = z.infer<typeof dashboardShareSchema>;

export const scopeBindingSchema = z.object({
  mode: z.enum(['fixed', 'variable']).default('variable'),
  fieldId: objectIdSchema.nullable().optional(),
  wellId: objectIdSchema.nullable().optional(),
});
export type ScopeBinding = z.infer<typeof scopeBindingSchema>;

export const sharingSchema = z.object({
  visibility: z.enum(['private', 'org', 'restricted']),
  shares: z.array(dashboardShareSchema).max(200).default([]),
  clientVisible: z.boolean().default(false),
  /** Client users it is shared with, or "all" = every client whose grants intersect the scope. */
  clientShares: z.union([z.literal('all'), z.array(objectIdSchema).max(200)]).default([]),
});
export type Sharing = z.infer<typeof sharingSchema>;

export const dashboardSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  ownerId: objectIdSchema,
  ownerName: z.string(),
  visibility: z.enum(['private', 'org', 'restricted']),
  clientVisible: z.boolean(),
  scopeBinding: scopeBindingSchema,
  tags: z.array(z.string()),
  version: z.number(),
  updatedAt: z.string(),
  canEdit: z.boolean(),
  canShare: z.boolean(),
});
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

export const dashboardSchema = dashboardSummarySchema.extend({
  doc: dashboardDocSchema,
  /** Only for callers who may share it. */
  sharing: sharingSchema.nullable(),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

export const createDashboardRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  fromTemplateId: objectIdSchema.optional(),
  /** Import: the document to start with (instead of a template). */
  doc: dashboardDocSchema.optional(),
  bind: scopeBindingSchema.optional(),
});

/**
 * A dashboard as a JSON file (export / import). `signals` names the signals of the document, so
 * an import into another organization can find its own signals by key or by name.
 */
export const DASHBOARD_FILE_KIND = 'twinlink.dashboard';
export const dashboardFileSchema = z.object({
  kind: z.literal(DASHBOARD_FILE_KIND),
  formatVersion: z.literal(1),
  exportedAt: z.string().max(40).optional(),
  /** Organization it was exported from. */
  orgId: objectIdSchema.optional(),
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  doc: dashboardDocSchema,
  signals: z.array(z.object({ id: objectIdSchema, key: z.string().max(200), name: z.string().max(200) })).max(1500).default([]),
});
export type DashboardFile = z.infer<typeof dashboardFileSchema>;
export const saveDashboardRequestSchema = z.object({
  doc: dashboardDocSchema,
  /** The version the edit was based on: a mismatch answers 409. */
  version: z.number().int().min(0),
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  note: z.string().max(200).optional(),
});
export const dashboardListQuerySchema = z.object({ filter: z.enum(['all', 'mine', 'shared', 'client']).default('all') });

export const dashboardVersionSchema = z.object({ version: z.number(), savedAt: z.string(), savedByName: z.string(), note: z.string().nullable() });
export const dashboardAccessEntrySchema = z.object({
  userId: objectIdSchema,
  name: z.string(),
  email: z.string(),
  role: orgRoleSchema,
  access: z.enum(['view', 'edit']),
  via: z.string(),
});

export const templateSchema = z.object({
  id: objectIdSchema,
  /** Null for global (SmarDac) templates. */
  orgId: objectIdSchema.nullable(),
  kind: z.enum(['dashboard', 'schematic']),
  name: z.string(),
  description: z.string(),
  requiredRoles: z.array(z.object({ equipmentTypeKey: z.string(), signalRole: z.string() })),
});
export type Template = z.infer<typeof templateSchema>;

/** Rename or describe a template (an organization's own, or a built-in one by the platform admin). */
export const updateTemplateRequestSchema = z
  .object({ name: z.string().trim().min(1).max(120).optional(), description: z.string().trim().max(500).optional() })
  .refine((v) => v.name !== undefined || v.description !== undefined, 'Nothing to change');
export type UpdateTemplateRequest = z.infer<typeof updateTemplateRequestSchema>;

/** Default dashboard for Live monitoring (doc 00 §5.3). */
export const liveTargetSchema = z.object({
  dashboardId: objectIdSchema.nullable(),
  source: z.enum(['test', 'well', 'org', 'none']),
});

// ---- Realtime (doc 08 §8) ----
export interface LiveItem {
  signalId: string;
  ts: number | null;
  value: number | boolean | string | null;
  quality: 'good' | 'stale' | null;
  unitId: string | null;
}
export interface SubscribeAck {
  subId: string;
  accepted: string[];
  denied: string[];
}
